import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import useSettings from '../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { dispatch, useSelector } from '../../../redux/store';
import { capitalize } from '../../../utils/common';
import DataGridTable from '../../../components/table/DataGridTable';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { addGuest, getGuest, updateGuest } from '../../../redux/slices/guestRedux/guestRedux';
import { getGuestCard } from '../../../redux/slices/guestRedux/guestCardRedux';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Container,
  Drawer,
  Grid,
  IconButton,
  Stack,
  SwipeableDrawer,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineContent from '@mui/lab/TimelineContent';
import moment from 'moment';

const Guest = () => {
  const pageName = 'Guests';
  const pageNameSingular = 'Guest';
  const { themeStretch } = useSettings();
  const dataModel = {
    name: '',
    phone: '',
    checkInDate: new Date().toISOString().split('T')[0],
    checkOutDate: new Date().toISOString().split('T')[0],
    guestCardId: '',
    userId: '',
  };

  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [logData, setLogData] = useState([]);
  const [historyDrawer, setHistoryDrawer] = useState(false);
  const [deletedOnly, setDeletedOnly] = useState(false);
  const { data, addData } = useSelector((state) => state?.guest);
  let cardList;
  ({ data: cardList } = useSelector((state) => state?.guestCard));

  useEffect(() => {
    dispatch(getGuest(deletedOnly));
    dispatch(getGuestCard());
  }, [deletedOnly]);

  useEffect(() => {
    setGuestList(data?.map((value) => ({ card: value?.guestCard?.code || 'Not Assigned', ...value, enteredBy: value?.user?.name })));
    setIsModelOpen(false);
  }, [data]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getGuest(deletedOnly));
      dispatch(getGuestCard());
    }
  }, [addData, deletedOnly]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedDataObj(dataModel);
      } else {
        setSelectedDataObj(modelData);
      }
    }
  };

  const updateEditingData = (key, value) => {
    setSelectedDataObj({
      ...selectedDataObj,
      [key]: value,
    });
  };

  const columns = !guestList?.[0]
    ? []
    : Object.keys(guestList?.[0])
      .map((value) => {
        if (['guestCard', 'user', 'log', 'userId', 'guestCardId', 'billing', 'payments', 'isDeleted'].includes(value)) {
          return {};
        } else {
          return {
            accessorKey: value,
            header: capitalize(value),
          };
        }
      })
      ?.filter((value) => value?.accessorKey);

  const handleSubmit = () => {
    if (selectedDataObj.name.length === 0) {
      toast.error('Name is required!');
    } else {
      const updatedData = {
        ...selectedDataObj,
        checkInDate: selectedDataObj.checkInDate || new Date().toISOString().split('T')[0],
        checkOutDate: selectedDataObj.checkOutDate || new Date().toISOString().split('T')[0],
      };
      dispatch(isAdd ? addGuest([updatedData]) : updateGuest(updatedData));
    }
  };

  const handleFilterChange = (_, v) => {
    if (!v) return;
    setDeletedOnly(v === 'deleted');
  };

  return (
    <Page title={`${pageName}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`${pageName}`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Product', href: '' },
            { name: `${pageName}` },
          ]}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                size="small"
                value={deletedOnly ? 'deleted' : 'active'}
                exclusive
                onChange={handleFilterChange}
                aria-label="guest filter"
              >
                <ToggleButton value="active" aria-label="active">
                  <Iconify icon="mdi:account-multiple-outline" style={{ marginRight: 6 }} />
                  Active
                </ToggleButton>
                <ToggleButton value="deleted" aria-label="deleted">
                  <Iconify icon="mdi:trash-can-outline" style={{ marginRight: 6 }} />
                  Deleted
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => {
                  manageModel(dataModel, 'add');
                }}
              >
                New {`${pageName}`}
              </Button>
            </Stack>
          }
        />

        <DataGridTable
          name={`${pageName} List`}
          data={guestList}
          column={columns}
          onRowClick={() => {}}
          isRowClickable={false}
          isLoading={false}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          enableRowSelection={true}
          enableRowActions={true}
          renderRowActionItems={(row) => [
            <>
              <Tooltip title={`Edit`}>
                <IconButton
                  color={'warning'}
                  size="small"
                  onClick={() => {
                    manageModel(row?.original, 'edit');
                  }}
                >
                  <Iconify icon={'line-md:edit-twotone'} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`View History`}>
                <IconButton
                  style={{ color: '#00a39e' }}
                  size="small"
                  onClick={() => {
                    setLogData(row?.original?.log);
                    setHistoryDrawer(true);
                  }}
                >
                  <Iconify icon={'material-symbols:history'} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`Delete This Guest`}>
                <IconButton
                  style={{ color: '#cd0000' }}
                  size="small"
                  onClick={() => {
                    if (row?.original?.card === 'Not Assigned') {
                      dispatch(updateGuest({ ...row?.original, isDeleted: true }));
                    } else {
                      toast.error('Card should be removed!');
                    }
                  }}
                >
                  <Iconify icon={'line-md:trash'} />
                </IconButton>
              </Tooltip>
            </>,
          ]}
        />

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} {pageNameSingular}</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant="subtitle1" padding="10px 0">
                    {`${pageNameSingular}`} Information
                  </Typography>
                  <Stack spacing={3}>
                    <TextField fullWidth label="Name" value={selectedDataObj?.name} onChange={(e) => updateEditingData('name', capitalize(e.target.value))} />
                    <TextField fullWidth label="Phone" value={selectedDataObj?.phone} onChange={(e) => updateEditingData('phone', capitalize(e.target.value))} />
                    <TextField fullWidth label="Check In Date" type={'date'} value={selectedDataObj?.checkInDate} onChange={(e) => updateEditingData('checkInDate', capitalize(e.target.value))} />
                    <TextField fullWidth label="Check Out Date" type={'date'} value={selectedDataObj?.checkOutDate} onChange={(e) => updateEditingData('checkOutDate', capitalize(e.target.value))} />
                    {cardList?.length > 0 && (
                      <Box display="flex" alignItems="center">
                        <Autocomplete
                          onChange={(event, newValue) => {
                            setSelectedDataObj({
                              ...selectedDataObj,
                              guestCardId: newValue?.value,
                              guestCard: { id: newValue?.value, code: cardList?.find((value) => value.id === newValue?.value)?.code },
                            });
                          }}
                          options={cardList
                            ?.filter((value) => value?.guests?.length === 0 || value?.guests === null)
                            ?.map((value) => ({
                              label: `${value.code}`,
                              value: value.id,
                            }))}
                          value={{
                            label: cardList?.find((value) => value.id === selectedDataObj?.guestCardId)?.code || 'Card Not Found',
                            value: selectedDataObj?.guestCardId,
                          }}
                          renderInput={(params) => <TextField label="Select Card" {...params} />}
                          sx={{ flex: 1 }}
                        />
                        {selectedDataObj?.guestCardId && (
                          <Button onClick={() => updateEditingData('guestCardId', null)} sx={{ ml: 2, minWidth: '30px', height: '40px' }} variant="outlined" color="error">
                            X
                          </Button>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    <LoadingButton type="submit" variant="contained" size="large" style={{ width: '100%' }} onClick={handleSubmit}>
                      {isAdd ? `Add ${pageNameSingular}` : `Save ${pageNameSingular}`}
                    </LoadingButton>
                    <Button
                      color="info"
                      variant="outlined"
                      size="large"
                      style={{ width: '100%' }}
                      onClick={() => {
                        manageModel(dataModel, 'add');
                      }}
                    >
                      Close
                    </Button>
                  </Stack>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </SwipeableDrawer>

        <Drawer anchor="right" open={historyDrawer} onClose={() => setHistoryDrawer(false)}>
          <Box sx={{ width: 400, padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Guest History
            </Typography>
            <div style={{ maxHeight: '90vh', overflowY: 'auto' }}>
              <Timeline position="alternate">
                {logData?.map((value) => (
                  <TimelineItem key={value.id}>
                    <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                      {moment(value?.datetime).format('YYYY-MM-DD HH:mm:ss')}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineConnector />
                      <TimelineDot>
                        <Iconify icon="eva:checkmark-circle-2-fill" />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: 2 }}>
                      <Typography variant="body1" component="span">
                        <b>{value?.title}</b>
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </div>
          </Box>
        </Drawer>
      </Container>
    </Page>
  );
};

export default Guest;
